import { useForm } from '../../context/FormContext'
import FormField from '../ui/FormField'
import Select from '../ui/Select'
import Button from '../ui/Button'
import ar from '../../locales/ar.json'
import { Phone } from 'lucide-react'

const t  = ar.step5
const ta = ar.app

const LOCATION_OPTIONS = [
  { value: 'داخل سوريا', label: t.inside_syria },
  { value: 'خارج سوريا', label: t.outside_syria },
  { value: 'غير معروف',  label: ta.unknown },
]
const SUPPORT_TYPES = Object.keys(t.supportTypes)
const BEST_TIME_OPTIONS = [
  { value: 'صباحاً',         label: t.time_morning },
  { value: 'ظهراً',          label: t.time_noon    },
  { value: 'مساءً',          label: t.time_evening },
  { value: 'أي وقت مناسب',   label: t.time_any     },
]

function ChildCard({ child, index, onChange, onRemove, errors }) {
  return (
    <div className="p-4 border border-stone-200 rounded-xl bg-stone-50 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-sm font-semibold text-primary-700">{t.childNumber} {index + 1}</h4>
        <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700 text-sm">
          ✕ حذف
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField id={`cname_${index}`} label={t.child_name}>
          <input
            id={`cname_${index}`}
            type="text"
            value={child.child_name}
            onChange={(e) => onChange('child_name', e.target.value)}
            className="input-base"
            dir="rtl"
          />
        </FormField>
        <FormField id={`cbirth_${index}`} label={t.child_birth_date}>
          <input
            id={`cbirth_${index}`}
            type="text"
            value={child.child_birth_date}
            onChange={(e) => onChange('child_birth_date', e.target.value)}
            className="input-base"
            placeholder="مثال: 2010 أو 2010-03-15"
            dir="ltr"
            style={{ textAlign: 'right' }}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Select id={`cloc_${index}`} label={t.child_location}
          value={child.child_location} onChange={(v) => onChange('child_location', v)}
          options={LOCATION_OPTIONS} placeholder={ta.select} />
        <Select id={`cprov_${index}`} label={t.child_has_provider}
          value={child.child_has_provider} onChange={(v) => onChange('child_has_provider', v)}
          options={[{ value: 'نعم', label: ta.yes }, { value: 'لا', label: ta.no }]}
          placeholder={ta.select} />
        <Select id={`csup_${index}`} label={t.child_support_needed}
          value={child.child_support_needed} onChange={(v) => onChange('child_support_needed', v)}
          options={[{ value: 'نعم', label: ta.yes }, { value: 'لا', label: ta.no }]}
          placeholder={ta.select} />
      </div>
    </div>
  )
}

export default function Step5_Family() {
  const { state, updateField, toggleArrayValue, addChild, updateChild, removeChild } = useForm()
  const { formData, errors } = state

  const showSpouseFields  = formData.spouseExists === 'نعم' || formData.marital_status === 'متزوج'
  const showChildrenSection = true // always visible

  return (
    <div>
      <h2 className="section-title">
        <span className="bg-primary-700 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">٥</span>
        {t.sectionTitle}
      </h2>

      {/* ─── Family Section ─── */}
      <div className="mb-6">
        <h3 className="text-base font-semibold text-stone-700 mb-4 flex items-center gap-2">
          <span className="text-gold">👨‍👩‍👧</span> {t.familySubtitle}
        </h3>

        {/* Spouse exists */}
        <div className="mb-4">
          <label className="label-base">{t.spouseExists}</label>
          <div className="flex gap-4 mt-1">
            {[{ v: 'نعم', l: ta.yes }, { v: 'لا', l: ta.no }].map(({ v, l }) => (
              <label key={v} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="spouseExists"
                  value={v}
                  checked={formData.spouseExists === v}
                  onChange={() => updateField('spouseExists', v)}
                  className="w-4 h-4 text-primary-700"
                />
                <span className="text-sm text-stone-700">{l}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Spouse fields */}
        {showSpouseFields && (
          <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl mb-4 space-y-0">
            <FormField id="spouse_fullname" label={t.spouse_fullname}>
              <input
                id="spouse_fullname"
                type="text"
                value={formData.spouse_fullname}
                onChange={(e) => updateField('spouse_fullname', e.target.value)}
                className="input-base"
                dir="rtl"
              />
            </FormField>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField id="spouse_birth_year" label={t.spouse_birth_year}>
                <input
                  id="spouse_birth_year"
                  type="text"
                  value={formData.spouse_birth_year}
                  onChange={(e) => updateField('spouse_birth_year', e.target.value)}
                  className="input-base"
                  placeholder="مثال: 1985"
                  dir="ltr"
                  style={{ textAlign: 'right' }}
                />
              </FormField>
              <Select
                id="spouse_current_location"
                label={t.spouse_current_location}
                value={formData.spouse_current_location}
                onChange={(v) => updateField('spouse_current_location', v)}
                options={LOCATION_OPTIONS}
                placeholder={ta.select}
              />
            </div>
            {formData.spouse_current_location === 'خارج سوريا' && (
              <FormField id="spouse_country" label={t.spouse_country}>
                <input
                  id="spouse_country"
                  type="text"
                  value={formData.spouse_country}
                  onChange={(e) => updateField('spouse_country', e.target.value)}
                  className="input-base"
                  dir="rtl"
                  placeholder="مثال: تركيا، ألمانيا..."
                />
              </FormField>
            )}
          </div>
        )}

        {/* Children */}
        {showChildrenSection && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
              <span className="text-gold">👶</span> {t.childrenSection}
            </h4>
            {formData.children.map((c, i) => (
              <ChildCard
                key={i}
                child={c}
                index={i}
                onChange={(field, val) => updateChild(i, field, val)}
                onRemove={() => removeChild(i)}
                errors={errors}
              />
            ))}
            <Button variant="secondary" size="sm" onClick={addChild} type="button">
              + {t.addChild}
            </Button>
          </div>
        )}

        {/* Alternative provider */}
        <Select
          id="alternative_provider_exists"
          label={t.alternative_provider_exists}
          value={formData.alternative_provider_exists}
          onChange={(v) => updateField('alternative_provider_exists', v)}
          options={[{ value: 'نعم', label: ta.yes }, { value: 'لا', label: ta.no }]}
          placeholder={ta.select}
        />

        {/* Support needs */}
        <div className="mb-4">
          <label className="label-base">{t.family_support_needs}</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
            {SUPPORT_TYPES.map((s) => (
              <label key={s} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-stone-50 border border-transparent hover:border-stone-200 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.family_support_needs.includes(s)}
                  onChange={() => toggleArrayValue('family_support_needs', s)}
                  className="w-4 h-4 text-primary-700 rounded border-stone-300"
                />
                <span className="text-sm text-stone-700">{t.supportTypes[s]}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Contact Section ─── */}
      <div className="pt-4 border-t border-stone-200">
        <h3 className="text-base font-semibold text-stone-700 mb-4 flex items-center gap-2">
          <Phone size={16} className="text-primary-600" /> {t.contactSubtitle}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField id="case_contact_person" label={t.case_contact_person} required error={errors.case_contact_person}>
            <input
              id="case_contact_person"
              type="text"
              value={formData.case_contact_person}
              onChange={(e) => updateField('case_contact_person', e.target.value)}
              className="input-base"
              dir="rtl"
            />
          </FormField>
          <FormField id="case_contact_relation" label={t.case_contact_relation}>
            <input
              id="case_contact_relation"
              type="text"
              value={formData.case_contact_relation}
              onChange={(e) => updateField('case_contact_relation', e.target.value)}
              className="input-base"
              dir="rtl"
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField id="contact_primary_phone" label={t.contact_primary_phone} required error={errors.contact_primary_phone}>
            <input
              id="contact_primary_phone"
              type="tel"
              value={formData.contact_primary_phone}
              onChange={(e) => updateField('contact_primary_phone', e.target.value)}
              className="input-base"
              dir="ltr"
              style={{ textAlign: 'right' }}
            />
          </FormField>
          <FormField id="contact_secondary_phone" label={t.contact_secondary_phone} error={errors.contact_secondary_phone}>
            <input
              id="contact_secondary_phone"
              type="tel"
              value={formData.contact_secondary_phone}
              onChange={(e) => updateField('contact_secondary_phone', e.target.value)}
              className="input-base"
              dir="ltr"
              style={{ textAlign: 'right' }}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Select id="contact_whatsapp" label={t.contact_whatsapp}
            value={formData.contact_whatsapp}
            onChange={(v) => updateField('contact_whatsapp', v)}
            options={[{ value: 'نعم', label: ta.yes }, { value: 'لا', label: ta.no }]}
            placeholder={ta.select} />
          <Select id="contact_best_time" label={t.contact_best_time}
            value={formData.contact_best_time}
            onChange={(v) => updateField('contact_best_time', v)}
            options={BEST_TIME_OPTIONS} placeholder={ta.select} />
          <Select id="contact_event_notifications" label={t.contact_event_notifications}
            value={formData.contact_event_notifications}
            onChange={(v) => updateField('contact_event_notifications', v)}
            options={[
              { value: 'نعم', label: t.notifications_yes },
              { value: 'لا', label: t.notifications_no },
              { value: 'رسائل فقط', label: t.notifications_messages_only },
            ]}
            placeholder={ta.select} />
        </div>

        <Select id="contact_for_verification_consent" label={t.contact_for_verification_consent}
          value={formData.contact_for_verification_consent}
          onChange={(v) => updateField('contact_for_verification_consent', v)}
          options={[{ value: 'نعم', label: ta.yes }, { value: 'لا', label: ta.no }]}
          placeholder={ta.select} />
      </div>
    </div>
  )
}
